import { Box, createStyles, makeStyles } from '@material-ui/core'
import Link from 'next/link'
import React, { useMemo } from 'react'
import { Colors, DynamicMuiIcon, Typography } from 'src/components/atoms'
import { ActionMenu, ActionMenuItemType } from 'src/components/molecules/ActionMenu'
import { DocumentFolder } from 'src/fixtures/types/folder'

const useStyles = makeStyles(() => {
  return createStyles({
    link: {
      textDecoration: 'none'
    },
    container: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '8px 12px',
      margin: '4px 8px',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      '&:hover': {
        backgroundColor: Colors.background.lightGray
      },
      '&:hover $menuButton': {
        opacity: 1
      }
    },
    activeContainer: {
      backgroundColor: '#E8E7FC',
      '& $folderIcon': {
        color: Colors.accent.keyPurple.default
      },
      '& $folderName': {
        fontWeight: 500,
        color: Colors.accent.keyPurple.default
      }
    },
    mainContent: {
      display: 'flex',
      alignItems: 'center',
      flex: 1,
      minWidth: 0
    },
    folderIcon: {
      flexShrink: 0,
      color: Colors.base.middleGray
    },
    folderName: {
      fontSize: '14px',
      color: Colors.base.black,
      fontWeight: 400,
      flex: 1,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      marginRight: '8px'
    },
    folderCount: {
      fontSize: '12px',
      color: Colors.base.middleGray,
      marginRight: '4px'
    },
    menuButton: {
      width: '24px',
      height: '24px',
      border: 'none',
      background: 'transparent',
      color: Colors.base.middleGray,
      cursor: 'pointer',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: 0,
      transition: 'all 0.2s',
      flexShrink: 0,
      '&:hover': {
        backgroundColor: Colors.background.gray,
        color: Colors.base.black
      }
    }
  })
})

export interface FolderItemProps {
  folder: DocumentFolder
  isActive?: boolean
  canManage?: boolean
  onEdit?: (folder: DocumentFolder) => void
  onDelete?: (folder: DocumentFolder) => void
}

export const FolderItem = ({ folder, isActive = false, canManage = false, onEdit, onDelete }: FolderItemProps) => {
  const classes = useStyles()

  const menuItemList: ActionMenuItemType[] = useMemo(
    () => [
      {
        label: '編集',
        icon: <DynamicMuiIcon variant="edit" size="16px" />,
        onClick: () => onEdit?.(folder)
      },
      {
        label: '削除',
        icon: <DynamicMuiIcon variant="delete" size="16px" />,
        onClick: () => onDelete?.(folder),
        color: Colors.accent.keyRed.default
      }
    ],
    [folder, onEdit, onDelete]
  )

  const path = `/documents?folder_id=${folder.id}`

  return (
    <Link href={path}>
      <a className={classes.link}>
        <Box className={`${classes.container} ${isActive ? classes.activeContainer : ''}`}>
          <Box className={classes.mainContent}>
            <Box className={classes.folderIcon}>
              <DynamicMuiIcon variant="folder" size="16px" />
            </Box>
            <Box ml="8px" />
            <Typography className={classes.folderName}>{folder.name}</Typography>
            <Typography className={classes.folderCount}>{folder.documentsCount}</Typography>
          </Box>
          {canManage && (
            <Box
              className={classes.menuButton}
              onClick={e => {
                e.preventDefault()
                e.stopPropagation()
              }}
            >
              <ActionMenu menuItemList={menuItemList} icon={<DynamicMuiIcon variant="dots" size="16px" />} />
            </Box>
          )}
        </Box>
      </a>
    </Link>
  )
}
